import Model from "../../../model/Model";
import Plan from "../../../model/Plan";
import {
  Box,
  FormLabel,
  Grid,
  IconButton,
  Link, Paper, TableContainer,
  Typography,
  useTheme
} from "@mui/material";
import React, {useEffect, useRef, useState} from "react";
import {
  FutureCalc,
  IBudgetCalc,
  IFutureYear,
  IMonteCarloResults,
  INetWorthCalc
} from "../../../components/calculator/Calculator";
import {useResources} from "../../../stores/ResourceProvider";
import Person from "../../../model/Person";
import TimelineChart from "../timeline/charts/TimelineChart";
import CountdownWidget from "./widgets/CountdownWidget";
import NetWorthProjectionChart from "../optimizer/charts/NetWorthProjectionChart";
import Snapshot from "../../../model/Snapshot";
import {dateToLocalFormat, numberToMoneyFormat} from "../../../stores/StoreUtilities";
import BudgetProjectionChart from "../optimizer/charts/BudgetProjectionChart";
import AnnualBudgetWidget from "./widgets/AnnualBudgetWidget";
import {TaxEstimate, TaxInput} from "../../../components/tax/TaxEstimator";
import FutureViewChart from "../optimizer/charts/FutureViewChart";
import LifetimeEstimatedTaxesChart from "../taxes/charts/LifetimeEstimatedTaxesChart";
import RetirementBenchmarksWidget from "./widgets/RetirementBenchmarksWidget";
import RiskAllocationPieChart from "../networth/charts/RiskAllocationPieChart";
import Asset, {AssetCategory} from "../../../model/Asset";
import {useReactToPrint} from "react-to-print";
import PrintIcon from "@mui/icons-material/Print";
import Logo from "../../../images/HeaderLogoPro.png";
import {SnapshotType, UpdateUserSettingInput, UserType} from "../../../API";
import NetWorthPrintReport from "../networth/charts/NetWorthPrintReport";
import BudgetPrintReport from "../budget/charts/BudgetPrintReport";
import FutureViewPrintReport from "../optimizer/charts/FutureViewPrintReport";
import InvestAd from "../../../components/marketing/InvestAd";
import MonteCarloChart from "../optimizer/charts/MonteCarloChart";
import RetirementConfidenceWidget from "./widgets/RetirementConfidenceWidget";
import RoutesConfig from "../../../RoutesConfig";
import {useNavigate} from "react-router-dom";
import {IOptimizerViewSettings, OptimizerChartView} from "../optimizer/OptimizerView";
import {ITaxesViewSettings, TaxesChartView} from "../taxes/TaxesView";
import UserSetting, {IQuickStartSettings, UserSettingType} from "../../../model/UserSetting";
import QuickStartWidget from "./widgets/QuickStartWidget";
import useIsMobile from "../../../components/useIsMobile";
import MobileDrawerButton from "../../../components/controls/MobileDrawerButton";
import ProjectionErrorAlert from "../ProjectionErrorAlert";


const DashboardView = ({
  model,
  plan,
  updatedAt,
  width,
  toggleDrawer
} : {
  model: Model
  plan: Plan
  updatedAt: string
  width: number
  toggleDrawer: () => void
}) => {
  const [isFree, setIsFree] = useState<boolean>(false)
  const [isPro, setIsPro] = useState<boolean>(false)
  const [currentYear, setCurrentYear] = useState<number>(0)
  const [currentModel, setCurrentModel] = useState<Model | undefined>()
  const [currentPlan, setCurrentPlan] = useState<Plan | undefined>()
  const [persons, setPersons] = useState<Person[]>([])
  const [netWorthCalcs, setNetWorthCalcs] = useState<INetWorthCalc[]>([])
  const [liquidAssets, setLiquidAssets] = useState<Asset[]>([])
  const [currentNetWorthCalc, setCurrentNetWorthCalc] = useState<INetWorthCalc | undefined>()
  const [currentBudgetCalc, setCurrentBudgetCalc] = useState<IBudgetCalc | undefined>()
  const [futureCalc, setFutureCalc] = useState<FutureCalc | undefined>()
  const [taxInput, setTaxInput] = useState<TaxInput | undefined>()
  const [taxEstimate, setTaxEstimate] = useState<TaxEstimate | undefined>()
  const [lifetimeTaxes, setLifetimeTaxes] = useState<number>(0)
  const [monteCarloResults, setMonteCarloResults] = useState<IMonteCarloResults | undefined>()
  const [calcError, setCalcError] = useState<string | undefined>()
  const [isProcessing , setIsProcessing] = useState<boolean>(false)
  const [quickStartUserSetting, setQuickStartUserSetting] = useState<UserSetting | undefined>()

  const { userStore, modelStore, calculator } = useResources()
  const isMobile = useIsMobile()

  const [isPrinting, setIsPrinting] = useState<boolean>(false)
  const promiseResolveRef = useRef<any>(null)
  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      promiseResolveRef.current();
    }
  }, [isPrinting]);

  const navigate = useNavigate()

  const isClient = userStore.user && userStore.user.userType === UserType.Client

  const theme = useTheme()
  const rightPanelWidth = isMobile ? 0 : width - 360 - 36

  const styles = {
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      color: theme.palette.grey["700"],
    },
    printReport: {
      '@media all': {
        display: "none"
      },
      '@media print': {
        display: "block",
        pageBreakBefore: "always"
      }
    },
    detailsLink: {
      fontSize: 14,
      cursor: "pointer",
      maxWidth: 80,
      textDecoration: "none",
    }
  }

  useEffect(() => {
    // Scroll to top
    setTimeout(() => {
      window.scrollTo(0, 0)
    }, 200);
  }, [])

  useEffect(() => {
    setIsFree(userStore.isFree)
    setIsPro(!userStore.isFree)
    setCurrentYear((new Date()).getFullYear())
    setCurrentModel(model)
    setCurrentPlan(plan)
    // loadSettings()
  }, [model, plan, updatedAt])

  useEffect(() => {
    if (currentModel) {
      setPersons(currentModel.persons.filter((p: Person) => p.id !== 'joint'))

      const liquid = currentModel.assets.filter((asset: Asset) => asset.assetCategory === AssetCategory.LiquidInvestableAssets)
      const real = currentModel.assets.filter((asset: Asset) => asset.assetCategory === AssetCategory.RealEstateAndProperty)
      const personal = currentModel.assets.filter((asset: Asset) => asset.assetCategory === AssetCategory.PersonalProperty)
      const other = currentModel.assets.filter((asset: Asset) => asset.assetCategory === AssetCategory.OtherAssets)
      const netWorthCalcs = currentModel.snapshots.map((snapshot: Snapshot) => {
        return calculator.calculateNetWorth(liquid, real, personal, other, currentModel.liabilities, snapshot)
      })
      setNetWorthCalcs(netWorthCalcs)
      setCurrentNetWorthCalc(netWorthCalcs[0])
      calculateFuture()
    }
  },[currentModel, currentPlan, updatedAt])

  useEffect(() => {
    if (currentModel) {
      const userSetting = userStore!.user!.getUserSetting(UserSettingType.quickStart, model.id)
      if (userSetting) {
        setQuickStartUserSetting(userSetting)
      }
    }
  }, [currentModel, updatedAt])

  const calculateFuture = async () => {
    if (!currentModel || !currentPlan) {
      return
    }

    const plan = currentPlan

    let maxEndYear: number = 0
    const latestLifeExpectancyDate = currentModel.getPlanEndDate(plan)
    if (latestLifeExpectancyDate) {
      const latestLifeExpectancyYear = latestLifeExpectancyDate.getFullYear()
      maxEndYear = latestLifeExpectancyYear
    }

    const multiModel = false

    const cloneModel = await modelStore.getModel(plan.modelId,false, true)
    if (cloneModel) {
      plan.applyChanges(cloneModel)
      modelStore.addCurrentSnapshot(cloneModel, SnapshotType.NetWorth)
      const label = multiModel ? `${cloneModel.name} - ${plan.name}` : plan.name
      const calc = calculator.calculateFuture(cloneModel, label, maxEndYear)
      // A projection that can't be calculated shows its error instead of any results, scores or print report
      setCalcError(calc?.calcError)
      if (calc?.calcError) {
        setFutureCalc(undefined)
        setCurrentBudgetCalc(undefined)
        setTaxInput(undefined)
        setTaxEstimate(undefined)
        setMonteCarloResults(undefined)
        return calc
      }
      if (calc) {
        setFutureCalc(calc)
        setCurrentBudgetCalc(calc.futureYears[0].budgetCalc)
        setTaxInput(calc.futureYears[0].taxInput)
        setTaxEstimate(calc.futureYears[0].taxEstimate)
        // const taxes = calc.futureYears.reduce((accum: number, fy: IFutureYear) => accum + fy.taxEstimate!.totalEstimatedTax ?? accum, 0)
        setLifetimeTaxes(calc.totalTax)
        const assets = cloneModel.assets.filter((asset: Asset) => asset.assetCategory === AssetCategory.LiquidInvestableAssets)
        setLiquidAssets(assets)
      }

      if (isPro && (!monteCarloResults || monteCarloResults.updatedAt !== updatedAt)  && !isProcessing) {
        setIsProcessing(true)
        let timer = setTimeout(() => {
          calculateMonteCarlo(maxEndYear)
          clearTimeout(timer)
        }, 200)
      }

      return calc
    }
  }

  const calculateMonteCarlo = async (maxEndYear: number) => {
    console.log(`MonteCarlo: calculating`)
    const start = new Date()
    // console.profile("MonteCarlo")
    const results = await calculator.calculateMonteCarlo(model, plan, maxEndYear)
    // console.profileEnd("MonteCarlo")
    setMonteCarloResults(results.calcError ? undefined : results)
    if (results.calcError) setCalcError(results.calcError)
    setIsProcessing(false)
    const end = new Date()
    console.log(`MonteCarlo took ${(end.getTime() - start.getTime())/1000} seconds`)
  }

  const navigateToOptimizerChartView = (chartView: OptimizerChartView) => {
    if (isMobile) {
      // Just navigate to optimizer view
      navigate(`${RoutesConfig.calc.pathname}/optimizer`)
      return
    }
    // Set the optimizer chart view
    const storageKey = `${modelStore.currentUser?.id}.OptimizerViewSettings`
    let settings: IOptimizerViewSettings
    let data = window.localStorage.getItem(storageKey)
    if (data) {
      settings = JSON.parse(data)
      settings.chartView = chartView
      data = JSON.stringify(settings)
    } else {
      data = JSON.stringify({
        chartView: chartView
      })
    }
    window.localStorage.setItem(storageKey, data)
    navigate(`${RoutesConfig.calc.pathname}/optimizer`)
  }

  const navigateToTaxesChartView = (chartView: TaxesChartView) => {
    // Set the optimizer chart view
    const storageKey = "RBC.TaxesViewSettings"
    let settings: ITaxesViewSettings
    let data = window.localStorage.getItem(storageKey)
    if (data) {
      settings = JSON.parse(data)
      settings.expandLeftPanel = false
      settings.currentYear = new Date().getFullYear()
      settings.chartView = chartView
      data = JSON.stringify(settings)
    } else {
      data = JSON.stringify({
        expandLeftPanel: false,
        currentYear: new Date().getFullYear(),
        expandedSections: [],
        chartView: chartView
      })
    }
    window.localStorage.setItem(storageKey, data)
    navigate(`${RoutesConfig.calc.pathname}/taxes`)
  }

  const handleExit = async (settings: IQuickStartSettings) => {
    if (quickStartUserSetting) {
      if (settings.status === "Exited" && userStore.isAdvisorOrAdmin) {
        await userStore.deleteUserSetting(quickStartUserSetting.id)
        setQuickStartUserSetting(undefined)
      } else {
        const input: UpdateUserSettingInput = {
          id: quickStartUserSetting.id,
          settings: JSON.stringify(settings)
        }
        const update = await userStore.updateUserSetting(input)
        setQuickStartUserSetting(update)
      }
    }
  }

  const renderQuickstartStatus = () => {
    if (currentModel && quickStartUserSetting) {
      const status = quickStartUserSetting.settings["status"]
      if (status === "Active" || status === "Paused") {
        return <QuickStartWidget model={currentModel} quickStartUserSetting={quickStartUserSetting} showResume={true} onExit={handleExit}/>
      }
    }
    return null
  }

  const renderRetirementCountdown = () => {
    return (
      <Box display="flex" flexDirection="row" flexGrow={1} justifyContent="space-between" pl={0} mb={1} mr={0} pr={0}>
        <Box sx={{display:"flex", flexDirection:"column", flexGrow:0, width: { xs:width-16, sm:360}, minHeight:"100%"}}>
          <Typography variant="h3" color="primary">Retirement Countdown</Typography>
          <Box display="flex" paddingRight={2} flexGrow={1} width={360}>
            <CountdownWidget persons={persons}/>
          </Box>
          <Link variant="body2" onClick={() => navigate(`${RoutesConfig.calc.pathname}/timeline`)} style={styles.detailsLink}>See Details</Link>
        </Box>
        {!isMobile &&
          <Box display="flex" flexGrow={1}>
            <TimelineChart persons={persons} view="chart"/>
          </Box>
        }
      </Box>
    )
  }

  const renderRetirementConfidence = () => {
    if (monteCarloResults) {
      return (
        <Box display="flex" flexDirection="row" flexGrow={1} justifyContent="stretch" pl={0} mt={2} mb={1} mr={0} pr={0}>
          <Box sx={{display:"flex", flexDirection:"column", width: { xs:width-16, sm:360}, minHeight:"100%"}}>
            <Typography variant="h3" color="primary">Retirement Confidence</Typography>
            <Box display="flex" flexDirection="column" flexGrow={1} pr={1} pt={1} width="100%">
              <RetirementConfidenceWidget monteCarloResults={monteCarloResults}/>
              <Link variant="body2" onClick={() => navigateToOptimizerChartView(OptimizerChartView.MonteCarlo)} style={styles.detailsLink} sx={{mt:1}}>See Details</Link>
            </Box>
          </Box>
          {!isMobile &&
            <Box display="flex" flexGrow={1} justifyContent="stretch" sx={{overflowY:"hidden"}}>
              <MonteCarloChart year={currentYear} monteCarloResults={monteCarloResults} isProcessing={isProcessing} hideScore={true}
                               height="300" width={String(rightPanelWidth)}
                               onSettings={() => {/*setShowMonteCarloSettings(true)*/}} onRefresh={() => {/*handleRefresh()*/}}/>
            </Box>
          }
        </Box>
      )
    } else if (isPro){
      return (null
        // <Box display="flex" flexDirection="row" flexGrow={1} justifyContent="stretch" pl={0} mt={2} mb={1} mr={0} pr={0}>
        //   <Box sx={{display:"flex", flexDirection:"column", width: { xs:"100%", sm:360}, minHeight:"100%"}}>
        //     <Typography variant="h3" color="primary">Retirement Confidence</Typography>
        //   </Box>
        //   <Box display="flex" flexGrow={1} justifyContent="stretch" height="300" width={String(rightPanelWidth)}>
        //   </Box>
        // </Box>
      )
    }
  }

  const renderBenchmarks = () => {
    if (futureCalc && netWorthCalcs.length > 0) {
      if (isFree) {
        return (
          <Box sx={{display:"flex", flexDirection:"column", width: { xs:width-16, sm:360}, minHeight:"100%"}}>
            <Typography variant="h3" color="primary">Retirement Benchmarks</Typography>
            <Box display="flex" flexDirection="column" flexGrow={1} paddingRight={0} paddingTop={1} width="100%">
              <RetirementBenchmarksWidget futureCalc={futureCalc} netWorthCalc={netWorthCalcs[0]} width="348px"/>
            </Box>
          </Box>
        )
      } else {
        return (
          <Box display="flex" flexDirection="row" flexGrow={1} justifyContent="stretch" pl={0} mt={2} mb={1} mr={0} pr={0}>
            <Box sx={{display:"flex", flexDirection:"column", width: { xs:width-16, sm:360}, minHeight:"100%"}}>
              <Typography variant="h3" color="primary">Retirement Benchmarks</Typography>
              <Box display="flex" flexDirection="column" flexGrow={1} paddingRight={0} paddingTop={1} width="100%">
                <RetirementBenchmarksWidget futureCalc={futureCalc} netWorthCalc={netWorthCalcs[0]} width="348px"/>
                <Link variant="body2" onClick={() => navigateToOptimizerChartView(OptimizerChartView.NetLiquidAssets)} style={styles.detailsLink} sx={{mt:1}}>See Details</Link>
              </Box>
            </Box>
            {!isMobile &&
              <Box display="flex" flexGrow={1} justifyContent="stretch" sx={{overflowY:"hidden"}}>
                <TableContainer component={Paper} sx={{ paddingTop:1, justifyContent:"center", height: 300, width:"100%" }}>
                  <FutureViewChart year={currentYear} futureCalcs={[futureCalc]} height="300" width={String(rightPanelWidth)} title="Liquid Asset Projection"/>
                </TableContainer>
              </Box>
            }
          </Box>
        )
      }
    } else {
      return null
    }
  }


  const renderNetWorth = () => {
    if (futureCalc && netWorthCalcs.length > 0) {
      if (isFree) {
        return (
          <Box sx={{display:"flex", flexDirection:"column", flexGrow:0, width: { xs:width-16, sm:360}, minHeight:"100%"}}>
            <Typography variant="h3" color="primary">Net Worth {currentYear}</Typography>
            <Box display="flex" paddingRight={2} paddingTop={1}>
              <Typography variant="h2" color="secondary">
                {numberToMoneyFormat(netWorthCalcs[0].netWorthTotal, 0)}
              </Typography>
            </Box>
            {!isMobile &&
              <Box display="flex" flexGrow={1} paddingRight={0} paddingTop={1} justifyContent="stretch">
                <RiskAllocationPieChart liquidAssets={liquidAssets} height="348" width="348"/>
              </Box>
            }
          </Box>
        )
      } else {
        return (
          <Box display="flex" flexDirection="row" flexGrow={1} justifyContent="stretch" pl={0} mt={2} mb={1} mr={0} pr={0}>
            <Box sx={{display:"flex", flexDirection:"column", flexGrow:0, width: { xs:width-16, sm:360}, minHeight:"100%"}}>
              <Typography variant="h3" color="primary">Net Worth {currentYear}</Typography>
              <Box display="flex" paddingRight={2} paddingTop={1}>
                <Typography variant="h2" color="secondary">
                  {numberToMoneyFormat(netWorthCalcs[0].netWorthTotal, 0)}
                </Typography>
              </Box>
              <Box display="flex" flexGrow={1} flexDirection="column" width={360} paddingRight={0} paddingTop={1} justifyContent="flex-start" alignItems="center">
                <RiskAllocationPieChart liquidAssets={liquidAssets} height="348" width="348"/>
                {!isClient && netWorthCalcs.length > 0 && netWorthCalcs[0].netWorthTotal >= 500000 &&
                  <InvestAd/>
                }
              </Box>
              <Link variant="body2" onClick={() => navigate(`${RoutesConfig.calc.pathname}/networth`)} style={styles.detailsLink} sx={{mt:1}}>See Details</Link>
            </Box>
            {!isMobile &&
              <Box display="flex" flexGrow={1} justifyContent="stretch">
                <NetWorthProjectionChart futureCalc={futureCalc} year={currentYear} height="300" width={String(rightPanelWidth)} title="Lifetime Net Worth"/>
              </Box>
            }
          </Box>
        )
      }
    } else {
      return null
    }
  }

  const renderBudget = () => {
    if (futureCalc && currentBudgetCalc) {
      const allExpenses = currentBudgetCalc.totalExpenses + currentBudgetCalc.totalDeductions + currentBudgetCalc.totalEstimatedTaxes
      const netIncome = currentBudgetCalc.totalIncome - allExpenses

      if (isFree) {
        return (
          <Box
            sx={{display: "flex", flexDirection: "column", flexGrow: 0, width: {xs: width-16, sm: 360}, minHeight: "100%"}}>
            <Typography variant="h3" color="primary">Budget {currentYear}</Typography>
            <Box display="flex" paddingRight={2} paddingTop={1} flexDirection="row" justifyContent="space-between">
              <Box display="flex" flexDirection="column" width="30%">
                <FormLabel sx={styles.formLabel}>Annual Income</FormLabel>
                <Typography variant="h4" color="secondary" gutterBottom>
                  {numberToMoneyFormat(currentBudgetCalc.totalIncome, 0)}
                </Typography>
              </Box>
              <Box display="flex" flexDirection="column" width="30%">
                <FormLabel sx={styles.formLabel}>Annual Expenses</FormLabel>
                <Typography variant="h4" color="error" gutterBottom>
                  {numberToMoneyFormat(allExpenses, 0)}
                </Typography>
              </Box>
              <Box display="flex" flexDirection="column" width="40%">
                <FormLabel sx={styles.formLabel}>Annual Net Income</FormLabel>
                <Typography variant="h3" color={netIncome >= 0 ? "secondary" : "error"} gutterBottom>
                  {numberToMoneyFormat(netIncome, 0)}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" flexGrow={1} padding={0} justifyContent="stretch" maxHeight={250} width={360}>
              <AnnualBudgetWidget budgetCalc={currentBudgetCalc} view={"Annual"} height="220" width="348"/>
            </Box>
          </Box>
        )
      } else {
        return (
          <Box display="flex" flexDirection="row" flexGrow={1} justifyContent="space-between" pl={0} mt={2} mb={1}
               mr={0} pr={0}>
            <Box
              sx={{display: "flex", flexDirection: "column", flexGrow: 0, width: {xs: width-16, sm: 360}, minHeight: "100%"}}>
              <Typography variant="h3" color="primary">Budget {currentYear}</Typography>
              <Box display="flex" paddingRight={2} paddingTop={1} flexDirection="row" justifyContent="space-between">
                <Box display="flex" flexDirection="column" width="30%">
                  <FormLabel sx={styles.formLabel}>Annual Income</FormLabel>
                  <Typography variant="h4" color="secondary" gutterBottom>
                    {numberToMoneyFormat(currentBudgetCalc.totalIncome, 0)}
                  </Typography>
                </Box>
                <Box display="flex" flexDirection="column" width="30%">
                  <FormLabel sx={styles.formLabel}>Annual Expenses</FormLabel>
                  <Typography variant="h4" color="error" gutterBottom>
                    {numberToMoneyFormat(allExpenses, 0)}
                  </Typography>
                </Box>
                <Box display="flex" flexDirection="column" width="40%">
                  <FormLabel sx={styles.formLabel}>Annual Net Income</FormLabel>
                  <Typography variant="h3" color={netIncome >= 0 ? "secondary" : "error"} gutterBottom>
                    {numberToMoneyFormat(netIncome, 0)}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" flexGrow={1} padding={0} justifyContent="stretch" maxHeight={250} width={360}>
                <AnnualBudgetWidget budgetCalc={currentBudgetCalc} view={"Annual"} height="220" width="348"/>
              </Box>
              <Link variant="body2" onClick={() => navigate(`${RoutesConfig.calc.pathname}/budget`)} style={styles.detailsLink} sx={{mt:1}}>See Details</Link>
            </Box>
            {!isMobile &&
              <Box display="flex" flexGrow={1} maxHeight={350}>
                <BudgetProjectionChart futureCalc={futureCalc} year={currentYear} height="300"
                                       width={String(rightPanelWidth)} title="Lifetime Budget"/>
              </Box>
            }
          </Box>
        )
      }
    } else {
      return null
    }
  }

  const renderTaxes = () => {
    if (taxInput && taxEstimate && futureCalc) {
      return (
        <Box display="flex" flexDirection="row" flexGrow={1} justifyContent="stretch" pl={0} mt={2} mb={1} mr={0} pr={0}>
          <Box sx={{display:"flex", flexDirection:"column", flexGrow:0, width: { xs:width-16, sm:360}, minHeight:"100%"}}>
            <Typography variant="h3" color="primary">Taxes {currentYear} </Typography>
            <Box display="flex" paddingRight={2} paddingTop={1} flexDirection="row" justifyContent="space-between">
              <Box display="flex" flexDirection="column" width="33%">
                <FormLabel sx={styles.formLabel}>Total Income</FormLabel>
                <Typography variant="h4" color="secondary" gutterBottom>
                  {numberToMoneyFormat(taxEstimate.totalIncome, 0)}
                </Typography>
              </Box>
              <Box display="flex" flexDirection="column" width="33%">
                <FormLabel sx={styles.formLabel}>Adj Gross Income</FormLabel>
                <Typography variant="h4" color="secondary" gutterBottom>
                  {numberToMoneyFormat(taxEstimate.adjustedGrossIncome, 0)}
                </Typography>
              </Box>
              <Box display="flex" flexDirection="column" width="34%">
                <FormLabel sx={styles.formLabel}>Taxable Income</FormLabel>
                <Typography variant="h3" color="secondary" gutterBottom>
                  {numberToMoneyFormat(taxEstimate.taxableIncome, 0)}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" paddingRight={2} paddingTop={0} flexDirection="row" justifyContent="space-between">
              <Box display="flex" flexDirection="column" width="33%">
                <FormLabel sx={styles.formLabel}>Tax Liability</FormLabel>
                <Typography variant="h4" color="error" gutterBottom>
                  {numberToMoneyFormat(taxEstimate.taxLiability, 0)}
                </Typography>
              </Box>
              <Box display="flex" flexDirection="column" width="33%">
                <FormLabel sx={styles.formLabel}>Total Payments</FormLabel>
                <Typography variant="h4" color="secondary" gutterBottom>
                  {numberToMoneyFormat(taxEstimate.totalPayments, 0)}
                </Typography>
              </Box>
              {taxEstimate.amountOwed > 0 &&
                <Box display="flex" flexDirection="column" width="34%">
                  <FormLabel sx={styles.formLabel}>Amount Owed</FormLabel>
                  <Typography variant="h4" color="error" gutterBottom>
                    {numberToMoneyFormat(taxEstimate.amountOwed, 0)}
                  </Typography>
                </Box>
              }
              {taxEstimate.amountOwed <= 0 &&
                <Box display="flex" flexDirection="column" width="34%">
                  <FormLabel sx={styles.formLabel}>Refund Amount</FormLabel>
                  <Typography variant="h4" color="green" gutterBottom>
                    {numberToMoneyFormat(taxEstimate.amountOverpaid, 0)}
                  </Typography>
                </Box>
              }
            </Box>
            <Box display="flex" paddingRight={2} paddingTop={0} flexDirection="row" justifyContent="space-between">
              <Box display="flex" flexDirection="column" width="33%">
                <FormLabel sx={styles.formLabel}>Lifetime Taxes</FormLabel>
                <Typography variant="h4" color="error" gutterBottom>
                  {numberToMoneyFormat(lifetimeTaxes, 0)}
                </Typography>
              </Box>
            </Box>
            <Link variant="body2" onClick={() => navigateToTaxesChartView(TaxesChartView.LifetimeEstTax)} style={styles.detailsLink} sx={{mt:1}}>See Details</Link>
          </Box>
          {!isMobile &&
            <Box display="flex" flexGrow={1} width={800}>
              <LifetimeEstimatedTaxesChart year={currentYear} futureCalc={futureCalc} height="300" width={String(rightPanelWidth)} title="Lifetime Taxes"/>
            </Box>
          }
        </Box>
      )
    } else {
      return null
    }
  }

  // For Printing
  const printRef = useRef(null)

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    // onBeforePrint: () => {
    //   return new Promise((resolve) => {
    //     promiseResolveRef.current = resolve;
    //     setIsPrinting(true);
    //   });
    // },
    onBeforeGetContent: () => {
      return new Promise((resolve) => {
        promiseResolveRef.current = resolve;
        setIsPrinting(true)
      })
    },
    onAfterPrint: () => {
      promiseResolveRef.current = null;
      setIsPrinting(false)
    }
  })

  const getPageStyle = () => {
    return `@page { size: landscape; margin: 16px !important; }`;
  }

  const fullDate = currentModel ? dateToLocalFormat(currentModel.snapshots[0].date, "MMM d, yyyy") : ""

  return (
    <Box ref={printRef} sx={{
      display: "flex",
      flexGrow: 1,
      flexDirection: "column",
    }}>
      <style>{getPageStyle()}</style>
      <ProjectionErrorAlert message={calcError}/>
      <Box display="flex" justifyContent="stretch" alignItems="top" mt={1}>
        <Box sx={{display:"flex", flexDirection:"row", flexGrow:0, width: { xs:width-16, sm:360}, minHeight:"100%", justifyContent:"stretch", alignItems:"center"}}>
          {isMobile && <MobileDrawerButton onClick={toggleDrawer}/>}
          <Typography variant="h2" color="primary">Dashboard</Typography>
          {isPrinting && currentModel &&
            <>
              <Box display="flex" alignItems="baseline">
                <Box width={50}><FormLabel sx={styles.formLabel}>Date:</FormLabel></Box>
                <Box><Typography variant="body2">{fullDate}</Typography></Box>
              </Box>
              <Box display="flex" alignItems="baseline">
                <Box width={50}><FormLabel sx={styles.formLabel}>Name:</FormLabel></Box>
                <Box><Typography variant="body2">{modelStore.currentUser!.fullName}</Typography></Box>
              </Box>
              <Box display="flex" alignItems="baseline">
                <Box width={50}><FormLabel sx={styles.formLabel}>Model:</FormLabel></Box>
                <Box><Typography variant="body2">{currentModel!.name} ({currentModel!.appliedPlan?.name})</Typography></Box>
              </Box>
            </>
          }
        </Box>
        <Box display="flex" flexGrow={1}>
          {!isPrinting && !isMobile && renderQuickstartStatus()}
        </Box>
        <Box display="flex" flexGrow={0} justifyContent="flex-end" alignItems="center">
          {!isPrinting && !isMobile &&
              <IconButton
                aria-label="Button"
                color="primary"
                // sx={{height:24, width:24, padding:0 }}
                onClick={handlePrint}
              >
                <PrintIcon/>
              </IconButton>
          }
          {isPrinting &&
            <img src={Logo} alt="logo" height={50}/>
          }
        </Box>
      </Box>

      {isFree && !isMobile &&
        <Grid container spacing={0}>
          <Grid item xs={12}>
            {renderRetirementCountdown()}
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={4}>
            {renderBenchmarks()}
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={4}>
            {renderNetWorth()}
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={4}>
            {renderBudget()}
          </Grid>
        </Grid>
      }
      {!isFree && !isMobile &&
        <Box>
          {renderRetirementCountdown()}
          {renderBenchmarks()}
          {renderRetirementConfidence()}
          {renderNetWorth()}
          {renderBudget()}
          {renderTaxes()}
        </Box>
      }
      {isMobile &&
        <Box sx={{display:"flex", flexDirection:"column", alignItems:"center", mt:1}}>
          {renderRetirementCountdown()}
          <hr style={{width:"100%"}}/>
          {isPro && renderRetirementConfidence()}
          {isPro && <hr style={{width:"100%"}}/>}
          {renderBenchmarks()}
          <hr style={{width:"100%"}}/>
          {renderNetWorth()}
          <hr style={{width:"100%"}}/>
          {renderBudget()}
          <hr style={{width:"100%"}}/>
          {isPro && renderTaxes()}
        </Box>
      }
      {currentModel && futureCalc && [
        <Box sx={styles.printReport}>
          <NetWorthPrintReport model={currentModel} netWorthCalc={currentNetWorthCalc}/>
        </Box>,
        <Box sx={styles.printReport}>
          <BudgetPrintReport model={currentModel} budgetCalc={currentBudgetCalc}/>
        </Box>,
        <Box sx={styles.printReport}>
          <FutureViewPrintReport model={currentModel} futureCalc={futureCalc} width={1200} isPrinting={isPrinting}/>
          {/*<FutureViewChart year={currentYear} futureCalcs={[futureCalc]} height="300" width={String(width)} title=""/>*/}
        </Box>
      ]}
    </Box>
  )
}

export default DashboardView